/**
 * <s-theme-toggle> — switches the whole storefront between the light look and
 * an all-black, iOS-style dark mode. The choice sits on <html data-theme> (set
 * before first paint by the layout head, so pages never flash white) and is
 * remembered on this device. The switch cross-fades every element at once
 * with a view transition; without one, colors ease over instead.
 */
const KEY = 'distvnt:theme';
const SWITCH_MS = 650;

class SThemeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.sync();
    this.onClick = () => this.toggle();
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  get isDark() {
    return document.documentElement.dataset.theme === 'dark';
  }

  sync() {
    this.button.setAttribute('aria-pressed', String(this.isDark));
  }

  toggle() {
    const root = document.documentElement;
    const next = this.isDark ? 'light' : 'dark';
    const apply = () => {
      if (next === 'dark') root.dataset.theme = 'dark';
      else delete root.dataset.theme;
      this.sync();
      document.querySelectorAll('s-theme-toggle').forEach((el) => el !== this && el.sync?.());
    };
    try { localStorage.setItem(KEY, next); } catch {}

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { apply(); return; }

    root.dataset.themeSwitching = '';
    const done = () => delete root.dataset.themeSwitching;
    if (document.startViewTransition) {
      const vt = document.startViewTransition(apply);
      vt.finished.finally(done);
    } else {
      apply();
      setTimeout(done, SWITCH_MS);
    }
  }
}

if (!customElements.get('s-theme-toggle')) customElements.define('s-theme-toggle', SThemeToggle);
